import _, { isArray, isNil } from 'lodash';
import { EndpointType } from '../../common/types/EndpointType';
import { Column } from '../types/Column';
import { CellValueGroup } from '../types/CellValueGroup';
import { EndpointData } from '../types/EndpointData';

export const isHiddenValue = (values: any, hiddenValues: any) => {
  const hiddenValuesArray = Object.keys(hiddenValues)
    .filter((k) => hiddenValues[k])
    .map(String);
  let valuesArray = values;
  if (!isArray(valuesArray)) {
    valuesArray = [values];
  }
  valuesArray = valuesArray.map((str: number | string) => {
    return String(str);
  });

  return valuesArray.length > 0 && _.difference(valuesArray, hiddenValuesArray).length === 0;
};

export const getTableData = (data: EndpointType[], columns: Column[]): (EndpointData | null)[] => {
  const groupedColumn = columns[0];
  const allDomainRows = data.map((domain) => {
    const isHidden = isHiddenValue(groupedColumn.getValues(domain), groupedColumn.hiddenValues);
    if (!isHidden) {
      const rows = getDomainRows(domain, columns);
      if (rows.length === 0) {
        return null;
      }
      return {
        id: domain.id,
        groupedColumnName: groupedColumn.name,
        groupedColumnValue: groupedColumn.getValues(domain),
        groupedColumnAdditionalValue: groupedColumn.getAdditionalValues?.(domain),
        groupedColumnComponent: groupedColumn.domainCellComponent ?? groupedColumn.cellComponent,
        totalConsumers: getTotalConsumersForDomain(domain),
        totalConsumptionVolume: getTotalConsumptionVolumeForDomain(domain),
        rows
      };
    }
    return null;
  });

  return allDomainRows.filter((item) => item) as EndpointData[];
};

const getDomainRows = (domain: EndpointType, columns: Column[]) => {
  const getColumns = columns
    .slice(1)
    .filter((column) => column.checked)
    .map((column) => getCells(column, domain))
    .values();

  const endpointIds = domain.endpoints.map((endpoint) => endpoint.id);

  const arrayGetColumns = Array.from(getColumns);
  const tempArray = [];
  if (arrayGetColumns && isArray(arrayGetColumns[0].columnValue)) {
    const rowCount = arrayGetColumns[0].columnValue.length;

    for (let index = 0; index < rowCount; index += 1) {
      const isHidden = arrayGetColumns.reduce(
        (result, column) => result || isHiddenValue(column.columnValue[index], column.hiddenValues),
        false
      );
      if (!isHidden) {
        tempArray.push(
          arrayGetColumns.map((e) => {
            return {
              columnName: e.columnName,
              columnValue: getColumnValue(e.columnName, e.columnValue[index]),
              columnComponent: e.columnComponent
            };
          })
        );
      }
    }
  }

  return tempArray.map((column, i) => {
    return {
      id: endpointIds[i],
      columns: column
    };
  });
};

const getColumnValue = (columnName: CellValueGroup, columnValue: CellValueGroup): unknown => {
  if (columnName === 'METHOD' && !columnValue) {
    return '-';
  }

  if (Array.isArray(columnValue)) {
    return columnValue.length > 0 ? columnValue : [''];
  }

  return isNil(columnValue) ? '' : columnValue;
};

const getCells = (column: Column, domain: EndpointType) => {
  let values = column.getValues(domain);

  if (!isArray(values)) {
    values = [values];
  }

  return {
    columnName: column.name,
    columnValue: values,
    columnComponent: column.cellComponent,
    hiddenValues: column.hiddenValues
  };
};

const getTotalConsumptionVolumeForDomain = (domain: EndpointType) =>
  domain.endpoints.reduce(
    (pre, cur) => pre + cur.consumers.reduce((previousValue, currentValue) => previousValue + currentValue.volume, 0),
    0
  );

// unique consumer count for each domain (show under domain name)
const getTotalConsumersForDomain = (domain: EndpointType) =>
  domain.endpoints
    .flatMap((endpoint) => endpoint.consumers.map((consumer) => consumer.name))
    .filter((value, index, self) => self.indexOf(value) === index).length;

export const getTotalConsumptionVolumeForSelectedDomains = (domains: EndpointData[]) =>
  domains.reduce((pre, cur) => pre + cur.totalConsumptionVolume, 0);

// unique consumer count for selected domain (show in footer)
export const getTotalConsumersForSelectedDomains = (domains: EndpointData[]) => {
  const uniqueConsumers: { [key: string]: boolean } = {};

  // Iterate through the data and collect unique consumers
  domains.forEach((item) => {
    item.rows.forEach((row) => {
      row.columns.forEach((col) => {
        if (col.columnName === 'CONSUMER') {
          const consumers = col.columnValue;
          if (Array.isArray(consumers)) {
            consumers.forEach((consumer) => {
              if (typeof consumer === 'string' && consumer.trim() !== '') {
                uniqueConsumers[consumer] = true;
              }
            });
          } else if (typeof consumers === 'string' && consumers.trim() !== '') {
            uniqueConsumers[consumers] = true;
          }
        }
      });
    });
  });

  // Get the count of unique consumers
  return Object.keys(uniqueConsumers).length;
};
